import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Paper,
  TextField,
  IconButton,
  Avatar,
  Badge,
  Chip,
  useTheme,
  alpha,
  Stack,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
  Divider,
  InputAdornment,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  Send,
  Search,
  Circle,
  Chat as ChatIcon,
  Person,
  Business,
  AccessTime,
  AttachFile,
  MoreVert,
  Refresh,
  CallEnd,
  CheckCircle,
  Close,
  Group,
  PendingActions,
  History,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { GlassCard } from '../components/ui/GlassCard';
import { socketService } from '../services/socket';
import { format, formatDistanceToNow, isToday, isYesterday } from 'date-fns';
import { useLayout } from '../contexts/LayoutContext';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

interface Chat {
  id: string;
  partner_id: string;
  subject: string;
  created: Date;
  updated: Date;
  owner_id: string;
  lastmessage?: string;
  participants?: ChatParticipant[];
  unreadCount?: number;
  lastMessageTime?: Date;
  status?: 'active' | 'pending' | 'closed';
}

interface ChatParticipant {
  id: string;
  chat_id: string;
  participant_type: number; // 1 = engineer, 2 = agent/user
  participant_id: string;
  joined: Date;
  departed?: Date;
  idle: number;
  name?: string;
  email?: string;
  isOnline?: boolean;
}

interface ChatMessage {
  id: string;
  chat_id: string;
  participant_id: string;
  message: string;
  created: Date;
  messageindex: number;
  senderName?: string;
  senderType?: 'engineer' | 'user' | 'system';
}

interface ChatRequest {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  companyName: string;
  message: string;
  created: Date;
}

const ChatNew: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { preferences } = useLayout();
  const isSidebarLayout = preferences.layoutMode === 'sidebar';
  const user = useSelector((state: RootState) => state.auth.user);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Get params from URL
  const clientId = searchParams.get('clientId');
  const userId = searchParams.get('userId');

  // State
  const [activeChats, setActiveChats] = useState<Chat[]>([]);
  const [pendingRequests, setPendingRequests] = useState<ChatRequest[]>([]);
  const [chatHistory, setChatHistory] = useState<Chat[]>([]);
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messageInput, setMessageInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(null);

  // Fetch active chats
  const fetchActiveChats = useCallback(() => {
    const socket = socketService.getSocket();
    if (!socket) return;

    socket.emit('getActiveChats', {}, (response: any) => {
      if (response.success && response.data) {
        setActiveChats(response.data);
        // Auto-select first chat if none selected
        if (!selectedChat && response.data.length > 0) {
          handleChatSelect(response.data[0]);
        }
      }
    });
  }, [selectedChat]);

  // Fetch pending chat requests
  const fetchPendingRequests = useCallback(() => {
    const socket = socketService.getSocket();
    if (!socket) return;

    socket.emit('getPendingChatRequests', {}, (response: any) => {
      if (response.success && response.data) {
        setPendingRequests(response.data);
      }
    });
  }, []);

  // Fetch messages for selected chat
  const fetchMessages = useCallback((chatId: string) => {
    const socket = socketService.getSocket();
    if (!socket) return;

    setLoading(true);
    socket.emit('getChatMessages', { chatId }, (response: any) => {
      if (response.success && response.data) {
        setMessages(response.data);
        scrollToBottom();
      }
      setLoading(false);
    });
  }, []);

  // Initialize on mount
  useEffect(() => {
    fetchActiveChats();
    fetchPendingRequests();

    // Set up real-time listeners
    const socket = socketService.getSocket();
    if (!socket) return;

    // Listen for new messages
    const handleNewMessage = (message: any) => {
      if (message.chat_id === selectedChat?.id) {
        setMessages(prev => [...prev, message]);
        scrollToBottom();
      }
      // Update unread count for chat list
      setActiveChats(prev => prev.map(chat => 
        chat.id === message.chat_id 
          ? { ...chat, lastmessage: message.message, lastMessageTime: new Date(), unreadCount: (chat.unreadCount || 0) + 1 }
          : chat
      ));
    };

    // Listen for new chat requests
    const handleChatRequest = (request: any) => {
      setPendingRequests(prev => [...prev, request]);
    };

    // Listen for typing indicators
    const handleUserTyping = (data: { userId: string; chatId: string; isTyping: boolean }) => {
      if (data.chatId === selectedChat?.id && data.userId !== user?.id) {
        setIsTyping(data.isTyping);
      }
    };

    socket.on('newChatMessage', handleNewMessage);
    socket.on('chatRequest', handleChatRequest);
    socket.on('userTyping', handleUserTyping);

    return () => {
      socket.off('newChatMessage', handleNewMessage);
      socket.off('chatRequest', handleChatRequest);
      socket.off('userTyping', handleUserTyping);
    };
  }, [selectedChat, user, fetchActiveChats, fetchPendingRequests]);

  // Auto-refresh chats every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchActiveChats();
      fetchPendingRequests();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchActiveChats, fetchPendingRequests]);

  // Start chat with specific user/client if provided
  useEffect(() => {
    if (userId || clientId) {
      // TODO: Initialize chat with specific user/client
      console.log('Starting chat with', { userId, clientId });
    }
  }, [userId, clientId]);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleChatSelect = (chat: Chat) => {
    setSelectedChat(chat);
    fetchMessages(chat.id);
    
    // Join chat room for real-time updates
    const socket = socketService.getSocket();
    socket?.emit('joinChat', { chatId: chat.id }, () => {});
    
    // Mark messages as read
    if (chat.unreadCount && chat.unreadCount > 0) {
      const messageIds = messages.slice(-chat.unreadCount).map(m => m.id);
      socket?.emit('markMessagesRead', { chatId: chat.id, messageIds }, () => {});
      
      // Update local state
      setActiveChats(prev => prev.map(c => 
        c.id === chat.id ? { ...c, unreadCount: 0 } : c
      ));
    }
  };

  const handleSendMessage = () => {
    if (!messageInput.trim() || !selectedChat || sending) return;

    setSending(true);
    const socket = socketService.getSocket();
    
    socket?.emit('sendChatMessage', {
      chatId: selectedChat.id,
      message: messageInput
    }, (response: any) => {
      if (response.success) {
        setMessageInput('');
        // Message will be added via real-time event
      }
      setSending(false);
    });
  };

  const handleTyping = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessageInput(e.target.value);
    
    // Send typing indicator
    const socket = socketService.getSocket();
    if (selectedChat && socket) {
      socket.emit('chatTyping', { chatId: selectedChat.id, isTyping: true });
      
      // Clear existing timeout
      if (typingTimeout) clearTimeout(typingTimeout);
      
      // Set new timeout to stop typing indicator
      const timeout = setTimeout(() => {
        socket.emit('chatTyping', { chatId: selectedChat.id, isTyping: false });
      }, 2000);
      setTypingTimeout(timeout);
    }
  };

  const handleAcceptRequest = (request: ChatRequest) => {
    const socket = socketService.getSocket();
    socket?.emit('acceptChatRequest', { requestId: request.id }, (response: any) => {
      if (response.success && response.data) {
        // Remove from pending
        setPendingRequests(prev => prev.filter(r => r.id !== request.id));
        // Add to active chats
        setActiveChats(prev => [...prev, response.data]);
        // Select the new chat
        handleChatSelect(response.data);
      }
    });
  };

  const handleEndChat = () => {
    if (!selectedChat) return;
    
    const socket = socketService.getSocket();
    socket?.emit('leaveChat', { chatId: selectedChat.id }, () => {
      // Remove from active chats
      setActiveChats(prev => prev.filter(c => c.id !== selectedChat.id));
      setSelectedChat(null);
      setMessages([]);
    });
  };

  const formatMessageTime = (timestamp: Date) => {
    const date = new Date(timestamp);
    if (isToday(date)) return format(date, 'HH:mm');
    if (isYesterday(date)) return `Yesterday ${format(date, 'HH:mm')}`;
    return format(date, 'dd/MM HH:mm');
  };

  const getParticipantName = (chat: Chat) => {
    const otherParticipant = chat.participants?.find(p => 
      p.participant_type === 2 // User/Agent type
    );
    return otherParticipant?.name || chat.subject || 'Unknown User';
  };

  const getParticipantStatus = (chat: Chat) => {
    const otherParticipant = chat.participants?.find(p => 
      p.participant_type === 2
    );
    return otherParticipant?.isOnline ? 'online' : 'offline';
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3, height: 'calc(100vh - 120px)' }}>
      {/* Header */}
      <Box sx={{ 
        mb: 3,
        mt: -3,
        pt: 3,
        ...(isSidebarLayout && {
          WebkitAppRegion: 'drag',
          '-webkit-app-region': 'drag',
        })
      }}>
        <Typography 
          variant="h4" 
          fontWeight={700} 
          gutterBottom
          sx={{
            display: 'inline-block',
            userSelect: 'text',
            WebkitAppRegion: 'no-drag',
            '-webkit-app-region': 'no-drag',
          }}
        >
          Support Chat
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{
            display: 'inline-block',
            userSelect: 'text',
            WebkitAppRegion: 'no-drag',
            '-webkit-app-region': 'no-drag',
          }}
        >
          Real-time messaging with customers
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ height: 'calc(100% - 60px)' }}>
        {/* Chat List */}
        <Grid size={{ xs: 12, md: 4 }}>
          <GlassCard sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 0 }}>
            {/* Tabs */}
            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tab 
                label="Active" 
                icon={<Badge badgeContent={activeChats.length} color="primary"><ChatIcon /></Badge>} 
                iconPosition="end"
              />
              <Tab 
                label="Pending" 
                icon={<Badge badgeContent={pendingRequests.length} color="error"><PendingActions /></Badge>} 
                iconPosition="end"
              />
              <Tab label="History" icon={<History />} iconPosition="end" />
            </Tabs>

            {/* Search */}
            <Box sx={{ p: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search chats..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Chat/Request List */}
            <List sx={{ flex: 1, overflow: 'auto' }}>
              {tabValue === 0 && activeChats.map((chat) => (
                <React.Fragment key={chat.id}>
                  <ListItemButton
                    selected={selectedChat?.id === chat.id}
                    onClick={() => handleChatSelect(chat)}
                  >
                    <ListItemAvatar>
                      <Badge
                        badgeContent={chat.unreadCount || 0}
                        color="error"
                        overlap="circular"
                      >
                        <Avatar>
                          <Person />
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={getParticipantName(chat)}
                      secondary={
                        <Stack spacing={0.5}>
                          <Typography variant="caption" noWrap>
                            {chat.lastmessage || 'No messages yet'}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Circle sx={{ fontSize: 8, color: getParticipantStatus(chat) === 'online' ? theme.palette.success.main : theme.palette.action.disabled }} />
                            <Typography variant="caption" color="text.secondary">
                              {chat.lastMessageTime ? formatDistanceToNow(new Date(chat.lastMessageTime)) + ' ago' : 'Just now'}
                            </Typography>
                          </Box>
                        </Stack>
                      }
                    />
                  </ListItemButton>
                  <Divider />
                </React.Fragment>
              ))}

              {tabValue === 1 && pendingRequests.map((request) => (
                <React.Fragment key={request.id}>
                  <ListItem
                    secondaryAction={
                      <Stack direction="row" spacing={1}>
                        <IconButton
                          edge="end"
                          color="success"
                          onClick={() => handleAcceptRequest(request)}
                        >
                          <CheckCircle />
                        </IconButton>
                        <IconButton
                          edge="end"
                          color="error"
                          onClick={() => setPendingRequests(prev => prev.filter(r => r.id !== request.id))}
                        >
                          <Close />
                        </IconButton>
                      </Stack>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: theme.palette.warning.main }}>
                        <PendingActions />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={request.userName}
                      secondary={
                        <Stack spacing={0.5}>
                          <Typography variant="caption">{request.companyName}</Typography>
                          <Typography variant="caption" noWrap>{request.message}</Typography>
                        </Stack>
                      }
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}

              {tabValue === 2 && chatHistory.length === 0 && (
                <ListItem>
                  <ListItemText
                    primary="No chat history"
                    secondary="Previous chats will appear here"
                  />
                </ListItem>
              )}
            </List>
          </GlassCard>
        </Grid>

        {/* Chat Area */}
        <Grid size={{ xs: 12, md: 8 }}>
          <GlassCard sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 0 }}>
            {selectedChat ? (
              <>
                {/* Chat Header */}
                <Box sx={{ 
                  p: 2, 
                  borderBottom: 1, 
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}>
                  <Avatar>{getParticipantName(selectedChat)[0]}</Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6">{getParticipantName(selectedChat)}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Circle sx={{ fontSize: 10, color: getParticipantStatus(selectedChat) === 'online' ? theme.palette.success.main : theme.palette.action.disabled }} />
                      <Typography variant="caption" color="text.secondary">
                        {getParticipantStatus(selectedChat)}
                      </Typography>
                      {isTyping && (
                        <Chip label="typing..." size="small" />
                      )}
                    </Box>
                  </Box>
                  <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                    <MoreVert />
                  </IconButton>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<CallEnd />}
                    onClick={handleEndChat}
                  >
                    End Chat
                  </Button>
                </Box>

                {/* Messages */}
                <Box sx={{ 
                  flex: 1, 
                  overflow: 'auto', 
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}>
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : messages.map((msg, index) => {
                    const isMyMessage = msg.participant_id === user?.loginId || msg.senderType === 'engineer';
                    return (
                      <motion.div
                        key={msg.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.02 }}
                        style={{
                          display: 'flex',
                          justifyContent: isMyMessage ? 'flex-end' : 'flex-start',
                        }}
                      >
                        <Paper
                          sx={{
                            p: 1.5,
                            maxWidth: '70%',
                            backgroundColor: isMyMessage
                              ? alpha(theme.palette.primary.main, 0.1)
                              : alpha(theme.palette.background.paper, 0.8),
                          }}
                        >
                          <Typography variant="body2">{msg.message}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {formatMessageTime(msg.created)}
                          </Typography>
                        </Paper>
                      </motion.div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </Box>

                {/* Input */}
                <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      fullWidth
                      placeholder="Type a message..."
                      value={messageInput}
                      onChange={handleTyping}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      disabled={sending}
                      multiline
                      maxRows={3}
                    />
                    <IconButton>
                      <AttachFile />
                    </IconButton>
                    <Button
                      variant="contained"
                      endIcon={sending ? <CircularProgress size={16} /> : <Send />}
                      onClick={handleSendMessage}
                      disabled={!messageInput.trim() || sending}
                    >
                      Send
                    </Button>
                  </Stack>
                </Box>
              </>
            ) : (
              <Box sx={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center',
                gap: 2,
              }}>
                <ChatIcon sx={{ fontSize: 64, color: theme.palette.action.disabled }} />
                <Typography variant="h6" color="text.secondary">
                  Select a chat to start messaging
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Choose from active chats or accept a pending request
                </Typography>
              </Box>
            )}
          </GlassCard>
        </Grid>
      </Grid>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => {
          // TODO: View customer details
          setAnchorEl(null);
        }}>
          <Person sx={{ mr: 1 }} />
          View Customer
        </MenuItem>
        <MenuItem onClick={() => {
          // TODO: View tickets
          setAnchorEl(null);
        }}>
          <Business sx={{ mr: 1 }} />
          View Tickets
        </MenuItem>
      </Menu>
    </Container>
  );
};

export default ChatNew;